import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { userService } from '../../services'
import { useParams, Link } from 'react-router-dom'
import { useState } from 'react'
import PageLayout from '../../components/layout/page-layout'
import { FormInput } from '../../components/forms'
import { Container } from '../../components/ui/container'
import { Badge } from '../../components/ui/badge'
import { roleToText } from '../../helpers/enum-to-text'

export default function CompanyUsers() {
  const { t } = useTranslation()
  const { id } = useParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['company-users', id, page, search],
    queryFn: () =>
      userService.getUsers({
        page,
        limit: 10,
        search: search || undefined,
        companyId: id,
      }),
  })

  return (
    <PageLayout
      headerProps={{
        title: t('companies.manageUsersTitle'),
        subtitle: t('companies.subtitle'),
        variant: 'dashboard',
        actionButton: {
          text: t('companies.addUser'),
          to: `/companies/${id}/users/invite`,
        },
      }}
      loadingMessage={t('users.loading')}
      isLoading={isLoading}
      error={error ? t('errors.loadUsers') : undefined}
    >
      <Container>
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
          <FormInput
            label={t('common.search')}
            name='search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('users.searchPlaceholder')}
            className='max-w-md'
          />
        </div>

        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gray-50 '>
                <tr>
                  <th className='text-left p-4 font-medium text-gray-900 '>{t('common.name')}</th>
                  <th className='text-left p-4 font-medium text-gray-900 '>{t('common.email')}</th>
                  <th className='text-left p-4 font-medium text-gray-900 '>{t('common.role')}</th>
                  <th className='text-left p-4 font-medium text-gray-900 '>{t('common.status')}</th>
                  <th className='text-left p-4 font-medium text-gray-900 '>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {data?.users.map((user, index) => (
                  <tr
                    key={user.id}
                    className='hover:bg-gray-50 transition-colors duration-200'
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <td className='p-4'>
                      <div className='flex items-center space-x-3'>
                        <div className='w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold'>
                          {user.firstName.charAt(0)}
                          {user.lastName.charAt(0)}
                        </div>
                        <div>
                          <div className='font-medium text-gray-900 '>
                            {user.firstName} {user.lastName}
                          </div>
                          {user.phoneNumber && (
                            <div className='text-sm text-gray-600 '>{user.phoneNumber}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className='p-4 text-sm text-gray-900 '>{user.email}</td>
                    <td className='p-4'>
                      <Badge
                        title={roleToText(user.role)}
                        variant={
                          user.role === 'ADMIN'
                            ? 'warning'
                            : user.role === 'SUPERADMIN'
                              ? 'error'
                              : 'default'
                        }
                      />
                    </td>
                    <td className='p-4'>
                      <Badge
                        title={user.isActive ? t('common.active') : t('common.inactive')}
                        variant={user.isActive ? 'success' : 'error'}
                      />
                    </td>
                    <td className='p-4'>
                      <Link
                        to={`/users/${user.id}/edit`}
                        className='inline-flex items-center px-3 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors duration-200'
                      >
                        <svg
                          className='w-4 h-4 mr-2'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                          />
                        </svg>
                        {t('common.edit')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.total > data.limit && (
            <div className='flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 '>
              <div className='text-sm text-gray-600 '>
                {t('common.pageOf', {
                  page,
                  totalPages: Math.ceil(data.total / data.limit),
                  total: data.total,
                })}
              </div>
              <div className='flex gap-2'>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200'
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * data.limit >= data.total}
                  className='px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 rounded-lg transition-all duration-200 disabled:cursor-not-allowed'
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </Container>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        tbody tr {
          animation: slide-up 0.3s ease-out forwards;
        }
      `,
        }}
      />
    </PageLayout>
  )
}
